import { DiscountType } from "@/types";
import SwitchHandle from "@/components/switch";
import React from "react";
import dayjs from "dayjs";
import { useMediaQuery } from "@mui/material";
import { truncateText } from "@/utils/truncateText";
import { ConditionalTooltip } from "@/pages/discountManagement/components/ConditionalTooltip";

interface CouponProps {
  title: string;
  hotelId: string;
  discountCode: string;
  validDateStart: string;
  validDateEnd: string;
  usagePerCode: number | null;
  usagePerUser: number | null;
  discountType: DiscountType;
  active: boolean;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
  discountValue?: number;
}

const CouponCard: React.FC<CouponProps> = ({
  title,
  hotelId,
  discountCode,
  validDateStart,
  validDateEnd,
  usagePerCode,
  usagePerUser,
  discountType,
  active,
  onEdit,
  onRemove,
  onToggleActive,
  discountValue = 0,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  return (
    <div className="w-full rounded-xl bg-white px-3 py-5 text-sm shadow-md">
      <ConditionalTooltip text={title} maxLength={50} isMobile={isMobile}>
        <h2 className="mb-6 cursor-pointer text-lg font-semibold text-neutral-800">
          {truncateText(title, 50)}
        </h2>
      </ConditionalTooltip>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Discount code</span>
          <ConditionalTooltip
            text={discountCode}
            maxLength={15}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-neutral-800">
              {truncateText(discountCode, 15)}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Discount Amount</span>
          <span className="font-semibold text-neutral-800">
            {discountType === DiscountType.FLAT
              ? `$${discountValue}`
              : `${discountValue}%`}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Valid Date</span>
          <span className="font-semibold text-neutral-800">
            {dayjs(validDateStart).format("YYYY/MM/DD")} -{" "}
            {dayjs(validDateEnd).format("YYYY/MM/DD")}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Usage per code</span>
          <span className="font-semibold text-neutral-800">
            {usagePerCode === null ? "Unlimited" : usagePerCode}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Usage per user</span>
          <span className="font-semibold text-neutral-800">
            {usagePerUser === null ? "Unlimited" : usagePerUser}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Discount type</span>
          <span className="font-semibold text-neutral-800">
            {discountType === DiscountType.PERCENTAGE ? "Percentage" : "Fixed"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-neutral-700">Active</span>
          <SwitchHandle
            checked={active}
            onChange={(event) => onToggleActive(hotelId, event.target.checked)}
            className="!m-0"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-4">
        <button
          onClick={() => {
            document.body.scrollTop = 0; // For Safari
            document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
            setTimeout(() => {
              onEdit(hotelId);
            }, 100);
          }}
          className="flex-1 rounded-lg bg-neutral-100 px-4 py-2 font-semibold text-neutral-950 transition-colors duration-200"
        >
          Edit
        </button>
        <button
          onClick={() => onRemove(hotelId)}
          className="flex-1 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors duration-200"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default CouponCard;
